import * as React from "react";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";

import { cn } from "../../utils";

type NavTabsVariant = "default";

interface NavTabsContextValue {
  variant: NavTabsVariant;
}

const NavTabsContext = React.createContext<NavTabsContextValue | null>(null);

const useNavTabsContext = () => {
  const context = React.useContext(NavTabsContext);

  if (!context) {
    throw new Error(
      "useNavTabsContext must be used within a NavTabsContextProvider",
    );
  }

  return context;
};

interface NavTabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  variant?: NavTabsVariant;
}

const NavTabs = ({ variant = "default", ...props }: NavTabsProps) => (
  <NavTabsContext
    value={{
      variant,
    }}
  >
    <TabsPrimitive.Root {...props} />
  </NavTabsContext>
);
NavTabs.displayName = TabsPrimitive.Root.displayName;

const navTabsListVariants = cva(
  "af-flex af-w-min af-justify-center af-rounded-lg af-text-muted-foreground data-[orientation=vertical]:af-flex-col data-[orientation=vertical]:af-items-start data-[orientation=horizontal]:af-items-center data-[orientation=vertical]:af-justify-start",
  {
    variants: {
      variant: {
        default: "",
      },
    },
  },
);

const NavTabsList = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) => {
  const { variant } = useNavTabsContext();

  return (
    <TabsPrimitive.List
      className={cn(navTabsListVariants({ variant }), className)}
      {...props}
    />
  );
};
NavTabsList.displayName = TabsPrimitive.List.displayName;

const navTabsTriggerVariants = cva(
  "af-flex af-flex-col af-items-center af-justify-center af-whitespace-nowrap af-text-sm af-font-medium af-ring-offset-background af-transition-all focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring focus-visible:af-ring-offset-2 disabled:af-pointer-events-none disabled:af-opacity-50",
  {
    variants: {
      variant: {
        default:
          "af-relative af-px-3 af-py-2 after:af-absolute after:-af-bottom-1.5 after:af-left-0 after:af-h-0.5 after:af-w-full after:af-bg-primary data-[state=inactive]:af-font-normal data-[state=active]:af-text-foreground data-[state=inactive]:af-text-muted-foreground data-[state=active]:after:af-block data-[state=inactive]:after:af-hidden",
      },
    },
  },
);

type NavTabsTriggerProps = React.ComponentProps<typeof TabsPrimitive.Trigger>;

const NavTabsTrigger = ({
  className,
  children,
  ...props
}: NavTabsTriggerProps) => {
  const { variant } = useNavTabsContext();

  return (
    <TabsPrimitive.Trigger
      className={cn(navTabsTriggerVariants({ variant }), className)}
      {...props}
    >
      <div className="af-flex af-h-[0px] af-flex-row af-gap-2 af-overflow-y-hidden af-font-semibold af-opacity-0">
        {children}
      </div>
      <div className="af-flex af-w-full af-flex-row af-items-center af-justify-between af-gap-2">
        {children}
      </div>
    </TabsPrimitive.Trigger>
  );
};
NavTabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const NavTabsContent = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    className={cn(
      "af-bg-background af-text-foreground af-ring-offset-background focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring focus-visible:af-ring-offset-2",
      "data-[orientation=horizontal]:af-mt-2",
      className,
    )}
    {...props}
  />
);
NavTabsContent.displayName = TabsPrimitive.Content.displayName;

export { NavTabs, NavTabsList, NavTabsTrigger, NavTabsContent };
